import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import GoogleWalletCertificatePanel from '../components/GoogleWalletCertificatePanel';
import RequestPreviewDrawer from '../components/RequestPreviewDrawer';
import { ArrowRight, Check } from '../components/Icons';

const PROTOCOLS = { dc_api: 'DC-API', openid4vp: 'OID4VP', oidc: 'OIDC' };
const REGION_LABELS = { EU: 'EU', GLOBAL: 'Global', Global: 'Global', AU: 'Australia', SE: 'Sweden', NG: 'Nigeria', JP: 'Japan' };

// PID attributes that can be requested from EU wallets.
// Core attributes are always included; optional ones are togglable.
// IMPORTANT: only enable optional attributes if they are registered
// in your EUDI relying party access certificate.
const CORE_PID_ATTRIBUTES = [
  { id: 'given_name',      label: 'Given name' },
  { id: 'family_name',     label: 'Family name' },
  { id: 'birth_date',      label: 'Date of birth' },
  { id: 'issuing_country', label: 'Issuing country' },
];
const OPTIONAL_PID_ATTRIBUTES = [
  { id: 'age_over_18',        label: 'Age over 18' },
  { id: 'age_over_21',        label: 'Age over 21' },
  { id: 'birth_place',        label: 'Place of birth' },
  { id: 'nationality',        label: 'Nationality' },
  { id: 'address',            label: 'Address' },
  { id: 'document_number',    label: 'Document number' },
  { id: 'expiry_date',        label: 'Document expiry date' },
];

const DEFAULT_PID_ATTRIBUTES = CORE_PID_ATTRIBUTES.map(a => a.id);

// Wallets whose request can be previewed (API: POST /api/wallets/{id}/request-preview).
const PREVIEWABLE = ['eudi', 'google_wallet'];

export default function WalletHubScreen() {
  const [ecosystems, setEcosystems]         = useState([]);
  const [allowedAttrs, setAllowedAttrs]     = useState(null); // null = loading
  const [config, setConfig]                 = useState({
    enabled_wallets: [],
    allowed_origins: [],
    redirect_urls: [],
    pid_attributes: DEFAULT_PID_ATTRIBUTES,
  });
  const [saving, setSaving]           = useState(false);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [success, setSuccess]         = useState(null);
  const [previewWallet, setPreviewWallet] = useState(null);

  useEffect(() => {
    Promise.all([
      api.getWalletEcosystems(),
      api.getVerificationConfig(),
      api.getAllowedPidAttributes(),
    ])
      .then(([wallets, cfg, attrResp]) => {
        setEcosystems(wallets || []);
        setAllowedAttrs(attrResp?.allowed_pid_attributes || []);
        setConfig({
          enabled_wallets: cfg?.enabled_wallets || [],
          allowed_origins: cfg?.allowed_origins || [],
          redirect_urls:   cfg?.redirect_urls   || [],
          pid_attributes:  cfg?.pid_attributes  || DEFAULT_PID_ATTRIBUTES,
        });
      })
      .catch(() => {
        setAllowedAttrs([]);
        setEcosystems([
          { id: 'eudi',          name: 'EU Digital Identity Wallet', region: 'EU',     protocols: ['dc_api', 'openid4vp'], status: 'AVAILABLE', enabled_by_default: true  },
          { id: 'apple_wallet',  name: 'Apple Wallet',               region: 'GLOBAL', protocols: ['dc_api'],              status: 'AVAILABLE', enabled_by_default: true  },
          { id: 'google_wallet', name: 'Google Wallet',              region: 'GLOBAL', protocols: ['dc_api'],              status: 'PREVIEW',   enabled_by_default: false },
          { id: 'se_bankid',     name: 'Swedish BankID',             region: 'SE',     protocols: ['oidc'],                status: 'PREVIEW',   enabled_by_default: false },
          { id: 'au_mygov',      name: 'Australian myGovID',         region: 'AU',     protocols: ['dc_api'],              status: 'PREVIEW',   enabled_by_default: false },
        ]);
      })
      .finally(() => setLoading(false));
  }, []);

  const isEnabled = (id) => config.enabled_wallets.includes(id);

  const toggleWallet = (id) => {
    const next = isEnabled(id)
      ? config.enabled_wallets.filter(w => w !== id)
      : [...config.enabled_wallets, id];
    setConfig(c => ({ ...c, enabled_wallets: next }));
  };

  const isAttrEnabled = (id) => config.pid_attributes.includes(id);

  const toggleAttr = (id) => {
    const next = isAttrEnabled(id)
      ? config.pid_attributes.filter(a => a !== id)
      : [...config.pid_attributes, id];
    setConfig(c => ({ ...c, pid_attributes: next }));
  };

  const save = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      await api.updateVerificationConfig(config);
      setSuccess('Configuration saved.');
    } catch (e) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="vt-muted">Loading…</p>;

  // Available wallets first, previews after.
  const wallets = [...ecosystems].sort((a, b) => (a.status === 'PREVIEW') - (b.status === 'PREVIEW'));
  const enabledCount = config.enabled_wallets.length;

  return (
    <div>
      <div className="vt-pageHead vt-pageHeadRow">
        <div>
          <div className="vt-eyebrow">Identity verification</div>
          <h1 className="vt-pageTitle">Wallet Hub</h1>
          <p className="vt-pageSub">Choose the wallets your users can verify with.</p>
        </div>
        <button className="vt-btn vt-btn--primary vt-btn--lg" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
          <span className="vt-btnArrow"><ArrowRight size={13} /></span>
        </button>
      </div>

      {error   && <div className="vt-alert vt-alert--error">{error}</div>}
      {success && <div className="vt-alert vt-alert--success"><Check size={16} /> {success}</div>}

      <div className="vt-stack">
        <section className="vt-panel">
          <div className="vt-panelHead">
            <div>
              <h2 className="vt-panelTitle">Wallets</h2>
              <p className="vt-panelSub">{enabledCount} of {ecosystems.length} enabled</p>
            </div>
          </div>
          <div className="vt-list">
            {wallets.map(eco => (
              <WalletRow key={eco.id} eco={eco} enabled={isEnabled(eco.id)} onToggle={() => toggleWallet(eco.id)}
                onPreview={PREVIEWABLE.includes(eco.id) ? () => setPreviewWallet(eco) : null} />
            ))}
          </div>
        </section>

        <section className="vt-panel">
          <div className="vt-panelHead">
            <div>
              <h2 className="vt-panelTitle">EU wallet attributes</h2>
              <p className="vt-panelSub">
                Always requested: {CORE_PID_ATTRIBUTES.map(a => a.label.toLowerCase()).join(', ').replace(/^./, c => c.toUpperCase())}.
              </p>
            </div>
          </div>
          <div className="vt-list">
            {OPTIONAL_PID_ATTRIBUTES.map(attr => {
              const onCert = allowedAttrs === null || allowedAttrs.includes(attr.id);
              return (
                <AttributeRow
                  key={attr.id}
                  attr={attr}
                  enabled={isAttrEnabled(attr.id)}
                  onToggle={() => toggleAttr(attr.id)}
                  notOnCert={!onCert}
                />
              );
            })}
          </div>
          <p className="vt-panelFoot">
            Optional fields must be on your EUDI certificate. To add one, contact{' '}
            <a href="mailto:team@veritage.io">team@veritage.io</a>.
          </p>
        </section>

        <GoogleWalletCertificatePanel />
      </div>

      {previewWallet && (
        <RequestPreviewDrawer
          wallet={previewWallet}
          pidAttributes={previewWallet.id === 'eudi' ? config.pid_attributes : null}
          onClose={() => setPreviewWallet(null)}
        />
      )}
    </div>
  );
}

function AttributeRow({ attr, enabled, notOnCert, onToggle }) {
  const on = enabled && !notOnCert;
  return (
    <div className={['vt-row', notOnCert ? 'vt-row--muted' : ''].filter(Boolean).join(' ')}>
      <div className="vt-rowBody">
        <div className="vt-rowTitle">{attr.label}</div>
        {notOnCert && <div className="vt-rowMeta">Not on your certificate</div>}
      </div>
      <button
        type="button"
        onClick={notOnCert ? undefined : onToggle}
        disabled={notOnCert}
        className={['vt-switch', on ? 'vt-switch--on' : ''].filter(Boolean).join(' ')}
        role="switch"
        aria-checked={on}
        aria-label={`Request ${attr.label}`}
      />
    </div>
  );
}

function WalletRow({ eco, enabled, onToggle, onPreview }) {
  const initials = eco.id === 'eudi' ? 'EU' : eco.name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const meta = [REGION_LABELS[eco.region] || eco.region, ...eco.protocols.map(p => PROTOCOLS[p] || p)].join(' · ');
  return (
    <div className="vt-row">
      <span className="vt-tile vt-tile--dark">{initials}</span>
      <div className="vt-rowBody">
        <div className="vt-rowTitle">{eco.name}</div>
        <div className="vt-rowMeta">{meta}</div>
      </div>
      {onPreview && <button type="button" className="vt-linkBtn" onClick={onPreview}>View request</button>}
      <button
        type="button"
        onClick={onToggle}
        className={['vt-switch', enabled ? 'vt-switch--on' : ''].filter(Boolean).join(' ')}
        role="switch"
        aria-checked={enabled}
        aria-label={enabled ? `Disable ${eco.name}` : `Enable ${eco.name}`}
      />
    </div>
  );
}
